import {
  Button,
  ColumnLayout,
  Container,
  FormField,
  SpaceBetween,
  StatusIndicator,
  TimeInput,
} from '@cloudscape-design/components';
import { ProcessNames } from '@parallel-fluidics/shared/constants';
import { useQuery } from '@tanstack/react-query';
import {
  buildChartOptions,
  buildTm3Datasets,
  createVerticalLinePlugin,
  normalizeTm3PlcRow,
  TM3_TUNING_SERIES,
} from 'components/ProcessData/chartShared';
import { useTheme } from 'features/theme/themeProvider';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PropTypes from 'prop-types';
import React, {
  useMemo,
  useState,
} from 'react';
import { Line } from 'react-chartjs-2';

const toDate = (value) => (
  typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
);

const formatTimeValue = (value) => {
  const date = toDate(value);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
};

const buildDateTime = (baseDate, timeValue) => {
  if (!baseDate || !timeValue) {
    return null;
  }

  const date = toDate(baseDate);
  const parsedDate = new Date(date);

  const [hours, minutes, seconds] = timeValue.split(':').map(Number);
  if ([hours, minutes, seconds].some((value) => Number.isNaN(value))) {
    return null;
  }

  parsedDate.setHours(hours, minutes, seconds, 0);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

export default function TuningHistoryGraph({
  startTime,
  endTime,
  pressId,
}) {
  const { isDarkMode } = useTheme();
  const [startTimeValue, setStartTimeValue] = useState(() => formatTimeValue(startTime));
  const [endTimeValue, setEndTimeValue] = useState(() => formatTimeValue(endTime));
  const [queryRange, setQueryRange] = useState(() => ({
    start: toDate(startTime),
    end: toDate(endTime),
  }));

  const editedStartDate = useMemo(
    () => buildDateTime(startTime, startTimeValue),
    [startTime, startTimeValue],
  );
  const editedEndDate = useMemo(
    () => buildDateTime(endTime, endTimeValue),
    [endTime, endTimeValue],
  );
  const rangeError = useMemo(() => {
    if (!editedStartDate || !editedEndDate) {
      return 'Enter a valid start and end time.';
    }
    if (editedStartDate >= editedEndDate) {
      return 'End time must be after start time.';
    }
    return null;
  }, [editedEndDate, editedStartDate]);

  const {
    data: plcData = [],
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: [
      'tuning-history-graph',
      pressId,
      queryRange.start?.toISOString(),
      queryRange.end?.toISOString(),
    ],
    queryFn: async () => {
      const functions = getFunctions();
      const { data } = await httpsCallable(functions, 'queryTM3ProcessDataV2')({
        startTime: queryRange.start,
        endTime: queryRange.end,
        press: pressId,
      });

      return data.map((row) => normalizeTm3PlcRow(row, ProcessNames.TUNING));
    },
    enabled: Boolean(pressId && queryRange.start && queryRange.end),
  });
  const plugin = useMemo(() => createVerticalLinePlugin(), []);
  const data = useMemo(() => ({
    labels: plcData.map(({ timestamp }) => timestamp),
    datasets: buildTm3Datasets({
      plcData,
      series: TM3_TUNING_SERIES,
      defaultVisibleLabels: ['Top Temp', 'Bottom Temp', 'Top Setpoint', 'Bottom Setpoint'],
    }),
  }), [plcData]);
  const options = useMemo(() => buildChartOptions({
    isDarkMode,
    isLoading: isPending,
  }), [isDarkMode, isPending]);

  if (isError) {
    return (
      <Container>
        <StatusIndicator type="error">
          {error?.message || 'Failed to load tuning graph.'}
        </StatusIndicator>
      </Container>
    );
  }

  return (
    <Container>
      <SpaceBetween size="m">
        <ColumnLayout columns={3}>
          <FormField label="Start time">
            <TimeInput
              value={startTimeValue}
              onChange={({ detail }) => setStartTimeValue(detail.value)}
              placeholder="hh:mm:ss"
              format="hh:mm:ss"
              use24Hour
            />
          </FormField>
          <FormField label="End time" errorText={rangeError}>
            <TimeInput
              value={endTimeValue}
              onChange={({ detail }) => setEndTimeValue(detail.value)}
              placeholder="hh:mm:ss"
              format="hh:mm:ss"
              use24Hour
            />
          </FormField>
          <FormField label={'\u00A0'}>
            <Button
              variant="normal"
              onClick={() => setQueryRange({ start: editedStartDate, end: editedEndDate })}
              disabled={Boolean(rangeError)}
            >
              Update Graph
            </Button>
          </FormField>
        </ColumnLayout>
        <Line options={options} data={data} plugins={[plugin]} />
      </SpaceBetween>
    </Container>
  );
}

TuningHistoryGraph.propTypes = {
  startTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      toDate: PropTypes.func.isRequired,
    }),
  ]).isRequired,
  endTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      toDate: PropTypes.func.isRequired,
    }),
  ]).isRequired,
  pressId: PropTypes.string.isRequired,
};
